import { IRewardsOrderFilter } from '../interfaces/rewards';
import { TRANSLATION_ORDER_HISTORY_DETAIL, TRANSLATION_HISTORY, TRANSLATION_POINTS } from '../translations/rewards';
import {
  RewardsOrderDetailStatus,
  RewardsOrderStatus,
  RewardsOrderTypes,
  RewardsOrderTypesEnum
} from '../types/rewards';
import { TagTypes } from '../types/types';
import { IDropzoneValidator } from './../interfaces/fileUploaderContainer';
import { REWARDS_POINTS_FILE_ROUTE, REWARDS_POINTS_ROUTE } from './auth';
import { TAG_TYPES } from './constants';
import {
  FILTER_ALL,
  FILTER_RECEIVED,
  FILTER_SENT,
  FILTER_VOIDED,
  FILTER_FAILED,
  FILTER_APPROVED,
  FILTER_IN_PROGRESS,
  FILTER_REJECTED,
  SECTION_BALANCE_LOADS,
  SECTION_POINTS
} from './tagManager';

export const ERROR_FILE_NAME = 'errors.xlsx';
export const POINTS_FILE_NAME = 'points.xlsx';

export const REWARDS_VOUCHER_UPLOADER_VALIDATORS: IDropzoneValidator = {
  maxFileCount: 1,
  maxFileSize: 1048576, // 1MB
  fileFormats: {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'application/pdf': ['.pdf']
  }
};

export const REWARDS_POINTS_UPLOADER_VALIDATORS: IDropzoneValidator = {
  maxFileCount: 1,
  maxFileSize: 6291456, // 6MB
  fileFormats: {
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
  }
};

export const REWARDS_HISTORY_DETAILS_FILTER = {
  all: {
    filter: TRANSLATION_ORDER_HISTORY_DETAIL.orderDetailsTable.status.all,
    gtmTag: FILTER_ALL,
    status: undefined
  },
  pending: {
    filter: TRANSLATION_ORDER_HISTORY_DETAIL.orderDetailsTable.status.pending,
    gtmTag: FILTER_SENT,
    status: 'pending'
  },
  assigned: {
    filter: TRANSLATION_ORDER_HISTORY_DETAIL.orderDetailsTable.status.assigned,
    gtmTag: FILTER_RECEIVED,
    status: 'assigned'
  },
  voided: {
    filter: TRANSLATION_ORDER_HISTORY_DETAIL.orderDetailsTable.status.voided,
    gtmTag: FILTER_VOIDED,
    status: 'voided'
  }
};
export const REWARDS_ORDER_TYPES: { [key: string]: RewardsOrderTypes } = {
  outgoings: 'outgoings',
  deposits: 'deposits'
};

export const REWARDS_ORDER_HISTORY_TYPE_TABS = [
  {
    id: RewardsOrderTypesEnum.outgoings,
    label: TRANSLATION_HISTORY.tabs.points,
    value: REWARDS_ORDER_TYPES.outgoings,
    gtmTag: SECTION_POINTS
  },
  {
    id: RewardsOrderTypesEnum.deposits,
    label: TRANSLATION_HISTORY.tabs.balance,
    value: REWARDS_ORDER_TYPES.deposits,
    gtmTag: SECTION_BALANCE_LOADS
  }
];

export const REWARDS_ORDER_BACKEND_ADMITTED_STATES = {
  done: 'done',
  invoicePending: 'invoice-pending',
  pendingPayment: 'pending-payment',
  pendingDelivery: 'pending-delivery',
  voided: 'voided'
};

export const REWARDS_ORDER_STATUS_FILTER: { [key: string]: IRewardsOrderFilter } = {
  all: {
    filter: TRANSLATION_HISTORY.table.allStatus,
    status: undefined,
    gtmTag: FILTER_ALL
  },
  sent: {
    filter: TRANSLATION_HISTORY.table.status.sent,
    status: REWARDS_ORDER_BACKEND_ADMITTED_STATES.done,
    tooltip: TRANSLATION_HISTORY.table.status.sentTooltip,
    gtmTag: FILTER_SENT
  },
  failed: {
    filter: TRANSLATION_HISTORY.table.status.failed,
    status: REWARDS_ORDER_BACKEND_ADMITTED_STATES.voided,
    tooltip: TRANSLATION_HISTORY.table.status.failedTooltip,
    gtmTag: FILTER_FAILED
  },
  approved: {
    filter: TRANSLATION_HISTORY.table.status.approved,
    status: REWARDS_ORDER_BACKEND_ADMITTED_STATES.done,
    gtmTag: FILTER_APPROVED
  },
  inProgress: {
    filter: TRANSLATION_HISTORY.table.status.inProgress,
    status: 'pending',
    gtmTag: FILTER_IN_PROGRESS
  },
  rejected: {
    filter: TRANSLATION_HISTORY.table.status.rejected,
    status: REWARDS_ORDER_BACKEND_ADMITTED_STATES.voided,
    gtmTag: FILTER_REJECTED
  }
};

export const REWARDS_ORDER_STATUS_MAPPING: { [key: string]: { [key: string]: RewardsOrderStatus } } = {
  [REWARDS_ORDER_TYPES.outgoings]: {
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.done]: 'sent',
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.invoicePending]: 'sent',
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.pendingPayment]: 'sent',
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.pendingDelivery]: 'sent',
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.voided]: 'failed'
  },
  [REWARDS_ORDER_TYPES.deposits]: {
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.done]: 'approved',
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.invoicePending]: 'inProgress',
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.pendingPayment]: 'inProgress',
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.pendingDelivery]: 'inProgress',
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.voided]: 'rejected'
  }
};

export const REWARDS_OUTGOINGS_ORDER_DETAILS: { [key: string]: string } = {
  points: TRANSLATION_HISTORY.table.details.outgoings.points,
  birthday: TRANSLATION_HISTORY.table.details.outgoings.birthday
};

export const REWARDS_ORDER_STATUS_TAG_TYPES = {
  sent: TAG_TYPES.informative,
  failed: TAG_TYPES.error,
  approved: TAG_TYPES.success,
  rejected: TAG_TYPES.error,
  inProgress: TAG_TYPES.neutral
} as { [key in RewardsOrderStatus]: TagTypes };

export const DEFAULT_REWARDS_ORDER_HISTORY_FILTER = {
  searchValue: '',
  type: REWARDS_ORDER_HISTORY_TYPE_TABS[0],
  status: REWARDS_ORDER_STATUS_FILTER.all
};

export const DEFAULT_PAGINATOR_STATE = 15;

export const ORDER_ASC = 'asc';
export const ORDER_DESC = 'desc';

export const REWARDS_ORDER_DETAILS_STATUS_TAG_TYPES = {
  assigned: TAG_TYPES.success,
  pending: TAG_TYPES.informative,
  voided: TAG_TYPES.error
} as { [key in RewardsOrderDetailStatus]: TagTypes };

export const REWARDS_ORDER_HISTORY_STATUS_TAG_TYPES = {
  [REWARDS_ORDER_TYPES.outgoings]: {
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.done]: TAG_TYPES.informative,
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.invoicePending]: TAG_TYPES.neutral,
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.pendingPayment]: TAG_TYPES.neutral,
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.pendingDelivery]: TAG_TYPES.neutral,
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.voided]: TAG_TYPES.error
  },
  [REWARDS_ORDER_TYPES.deposits]: {
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.done]: TAG_TYPES.success,
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.invoicePending]: TAG_TYPES.neutral,
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.pendingPayment]: TAG_TYPES.neutral,
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.pendingDelivery]: TAG_TYPES.neutral,
    [REWARDS_ORDER_BACKEND_ADMITTED_STATES.voided]: TAG_TYPES.error
  }
};

export const REWARDS_ORDER_HISTORY_POINTS_TOOLTIP_TEXT = {
  [REWARDS_ORDER_BACKEND_ADMITTED_STATES.done]: TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.tooltip.done,
  [REWARDS_ORDER_BACKEND_ADMITTED_STATES.invoicePending]: TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.tooltip.inProgress,
  [REWARDS_ORDER_BACKEND_ADMITTED_STATES.pendingPayment]: TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.tooltip.inProgress,
  [REWARDS_ORDER_BACKEND_ADMITTED_STATES.pendingDelivery]:
    TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.tooltip.inProgress,
  [REWARDS_ORDER_BACKEND_ADMITTED_STATES.voided]: TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.tooltip.voided
};

export const REWARDS_ORDER_HISTORY_BALANCE_STEPS = {
  [REWARDS_ORDER_BACKEND_ADMITTED_STATES.done]: 3,
  [REWARDS_ORDER_BACKEND_ADMITTED_STATES.invoicePending]: 0,
  [REWARDS_ORDER_BACKEND_ADMITTED_STATES.pendingPayment]: 1,
  [REWARDS_ORDER_BACKEND_ADMITTED_STATES.pendingDelivery]: 2,
  [REWARDS_ORDER_BACKEND_ADMITTED_STATES.voided]: 0
};

export const REWARDS_POINTS_TABS = [
  {
    id: 0,
    label: TRANSLATION_POINTS.tabs.file,
    pathName: REWARDS_POINTS_FILE_ROUTE
  },
  {
    id: 1,
    label: TRANSLATION_POINTS.tabs.send,
    pathName: REWARDS_POINTS_ROUTE
  }
];

export const POINTS_FILE_VALIDATION_ERRORS = {
  MAX_FILE_SIZE: 54,
  EMPTY_FILE_ERROR: 102,
  INVALID_FORMAT: 101
};
